import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useExperienceStorefront } from '../../components/website/StorefrontExperience';
import { fetchStorefrontDeveloper } from '../../services/storefrontAdminService';

const IdentityLine = ({ label, value }) => (
  <Box sx={{ py: 1.25, borderBottom: '1px solid #eef2f7' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography fontWeight={700} sx={{ wordBreak: 'break-all' }}>{value || '—'}</Typography>
  </Box>
);

export default function StorefrontDeveloper() {
  const storefront = useExperienceStorefront();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!storefront?.code) return;
    fetchStorefrontDeveloper(storefront.code)
      .then(setData)
      .catch((requestError) => setError(requestError.response?.data?.error || 'Failed to load API information.'));
  }, [storefront?.code]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" fontWeight={900}>Developer & API</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Read-only storefront identity and canonical headless endpoints. Storefront key and domain are managed through the storefront configuration, not here.
      </Typography>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/developer/api-catalog')}>
        Open Full API Catalog
      </Button>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={800}>Storefront Identity</Typography>
          <IdentityLine label="Storefront Key" value={data.storefront.code} />
          <IdentityLine label="Primary Domain" value={data.storefront.domain} />
        </CardContent>
      </Card>

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {Object.entries(data.endpoints || {}).map(([name, url]) => (
          <Card variant="outlined" key={name}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {name.replaceAll('_', ' ')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{url}</Typography>
                <Button startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard?.writeText(url)}>Copy</Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
